/**
 * Helpers for reading the linked-author field across content types.
 *
 * The schema definition for `author` is the `staffAuthor` object
 * (`{ display_name, user_id }`). Pre-migration content still stores
 * `author` as a plain string (the legacy "Lazarus Redmayne"-style
 * value imported from WordPress). These helpers transparently
 * accept either shape so the frontend can render bylines correctly
 * during and after the migration without per-page branching.
 */

export interface StaffAuthorShape {
  display_name?: string | null
  user_id?: string | null
}

/**
 * Extract the byline string for rendering ("By X").
 * Returns null when the field is empty / unparseable.
 */
export function authorDisplayName(
  author: string | StaffAuthorShape | null | undefined,
): string | null {
  if (!author) return null
  if (typeof author === 'string') return author.trim() || null
  if (typeof author === 'object' && author.display_name) {
    return String(author.display_name).trim() || null
  }
  return null
}

/**
 * Extract the linked staff user_id (Supabase profiles.id), if any.
 * Returns null for legacy string bylines or free-text authors.
 */
export function authorUserId(
  author: string | StaffAuthorShape | null | undefined,
): string | null {
  if (!author || typeof author === 'string') return null
  return author.user_id || null
}
